import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DataTable } from '@/components/DashCharts';
import { fetchBing, fetchBingStatus, saveBingKey, removeBingKey } from '@/lib/api';
import { formatMetric as fmt } from '@/lib/dashboardReports.mjs';

const KEY_HELP = 'https://www.bing.com/webmasters/settings/api';
const metric = (key, label, suffix = '') => ({ key, label, align: 'right', render: value => fmt(value, suffix) });
const queryColumns = [{ key: 'query', label: 'Bing query', wide: true }, metric('clicks', 'Clicks'), metric('impressions', 'Impressions'), metric('ctr', 'CTR', '%'), metric('position', 'Average position')];
const pageColumns = [{ key: 'page', label: 'Page', wide: true }, metric('clicks', 'Clicks'), metric('impressions', 'Impressions'), metric('ctr', 'CTR', '%'), metric('position', 'Average position')];

/**
 * Bing Webmaster Tools for the selected site. Users add their own Bing API
 * key once; it applies to every verified site in their Bing account.
 * onStatus reports a short label for the "Your data sources" list.
 */
export default function BingReport({ propertyId, range, isDemo, siteName, onStatus }) {
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    setError('');
    setReport(null);
    (async () => {
      try {
        const s = isDemo ? { set: false, demo: true } : await fetchBingStatus();
        if (!live) return;
        setStatus(s);
        if (!isDemo && !s.set) return;
        const r = await fetchBing(propertyId, range);
        if (live) setReport(r);
      } catch (e) {
        if (live) setError(e.response?.data?.detail || 'Bing data could not be loaded.');
      }
    })();
    return () => { live = false; };
  }, [propertyId, range, isDemo, reload]);

  useEffect(() => {
    onStatus?.(error ? 'Unavailable' : isDemo ? 'Demo data' : !status ? 'Loading' : !status.set ? 'Not connected' : !report ? 'Loading' : report.matched ? 'Report returned' : 'Site not found in Bing');
  }, [status, report, error, isDemo, onStatus]);

  const save = async e => {
    e.preventDefault();
    if (!key.trim()) return;
    setBusy(true);
    try {
      const s = await saveBingKey(key.trim());
      setKey('');
      setStatus(s);
      setReload(n => n + 1);
      toast.success(`Bing connected. ${s.verified_sites} verified site${s.verified_sites === 1 ? '' : 's'} found.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Couldn’t save the Bing key');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { setStatus(await removeBingKey()); setReport(null); toast.success('Bing key removed'); }
    catch { toast.error('Couldn’t remove the Bing key'); }
    finally { setBusy(false); }
  };

  const connected = isDemo || status?.set;
  return <section id="bing" className="ss-section" aria-labelledby="bing-heading" data-testid="bing-report">
    <h2 id="bing-heading">Bing search performance</h2>
    <p>{isDemo ? 'Demo data. ' : ''}Clicks and impressions from Bing Webmaster Tools, kept separate from Google. Bing data lags by a few days, so the period ends on the latest day Bing has reported.</p>
    {!connected && status && <div className="ss-panel ss-bing-connect">
      <form onSubmit={save} className="ss-bing-form">
        <label htmlFor="bing-key">Bing Webmaster API key</label>
        <div>
          <input id="bing-key" type="password" autoComplete="off" value={key} onChange={e => setKey(e.target.value)} placeholder="Paste your API key" data-testid="bing-key-input" />
          <button type="submit" disabled={busy || !key.trim()} data-testid="bing-key-save">{busy ? 'Checking…' : 'Connect Bing'}</button>
        </div>
      </form>
      <p className="ss-scope">In Bing Webmaster Tools, open Settings, then API access, and generate an API key. <a href={KEY_HELP} target="_blank" rel="noopener noreferrer">Open Bing Webmaster Tools</a>. SEO Signals checks the key with Bing, stores it encrypted and only reads data. One key covers every site verified in your Bing account.</p>
    </div>}
    {error && <p role="alert" data-testid="bing-error">{error}</p>}
    {connected && !error && !report && <p>Loading Bing data…</p>}
    {report && !report.matched && <div className="ss-panel"><p className="ss-scope" data-testid="bing-unmatched">None of the {report.verified_sites} verified sites in your Bing account match {siteName || 'this site'}. Add and verify it in Bing Webmaster Tools, then reload.</p></div>}
    {report?.matched && <>
      <dl className="ss-detail-metrics ss-bing-totals" data-testid="bing-totals">
        <div><dt>Bing clicks</dt><dd>{fmt(report.totals?.clicks)}</dd></div>
        <div><dt>Bing impressions</dt><dd>{fmt(report.totals?.impressions)}</dd></div>
        <div><dt>Bing CTR</dt><dd>{fmt(report.totals?.ctr, '%')}</dd></div>
        <div><dt>Period</dt><dd>{report.period ? `${report.period.start} to ${report.period.end}` : 'No data yet'}</dd></div>
      </dl>
      <div className="ss-panel">
        <DataTable testid="bing-queries-table" columns={queryColumns} rows={report.top_queries} emptyMessage="Bing returned no queries for this period." />
      </div>
      <div className="ss-panel ss-bing-pages">
        <DataTable testid="bing-pages-table" columns={pageColumns} rows={report.top_pages} emptyMessage="Bing returned no pages for this period." />
      </div>
      <p className="ss-scope">Source: {report.site_url}. Top 10 rows by impressions. Average position is weighted by impressions. CTR shows as unavailable where Bing reports more clicks than impressions.</p>
    </>}
    {!isDemo && status?.set && <p className="ss-bing-key">Using your Bing key ····{status.last4} <button type="button" onClick={remove} disabled={busy} data-testid="bing-key-remove">Remove key</button></p>}
  </section>;
}
